import { QueryClient } from '@tanstack/react-query';
import { SystemPermission_Permission } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { listEventsQuery } from '~/pages/Advanced/api/events.queries';
import { listMetricsQuery } from '~/shared/api/metrics.queries';
import { getSystemQuery } from '~/shared/api/system.queries';
import {
  checkIfUserHasPermissionForSystem,
  requireUserPermissionForSystemId,
} from '~/shared/auth/auth-utils';
import { assertIsDefined } from '~/types/assert-type';
import { getDeviceDetailsQuery } from '../../shared/api/deviceDetails.queries';
import { MetricsModel } from '~/shared/models/metrics/MetricsModel';
import { EventsModel } from '~/pages/Advanced/models/EventsModel';
import { DeviceDetailsModel } from '~/shared/models/system/DeviceDetailsModel';
import { SystemModel } from '~/shared/models/system/SystemModel';

export type AdvancedPageLoaderData = {
  metrics?: MetricsModel;
  deviceDetails?: DeviceDetailsModel;
  events?: EventsModel;
};

export const advancedLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
    request,
  }: LoaderFunctionArgs): Promise<AdvancedPageLoaderData | Response> => {
    const { productId, systemId } = params;

    assertIsDefined(productId);
    assertIsDefined(systemId);

    await requireUserPermissionForSystemId(
      queryClient,
      request,
      [
        SystemPermission_Permission.ADMIN,
        SystemPermission_Permission.COMMISSIONING,
      ],
      systemId,
    );

    const system = await queryClient.ensureQueryData(getSystemQuery(systemId));
    const systemModel = new SystemModel({ system });

    const product = systemModel.productById(productId);

    if (!product) {
      return redirect('/not-found');
    }

    const isAdmin = await checkIfUserHasPermissionForSystem(
      queryClient,
      request,
      systemId,
      SystemPermission_Permission.ADMIN,
    );

    const [metricsModel, deviceDetails, events] = await Promise.all([
      queryClient.ensureQueryData(
        listMetricsQuery({
          targets: product.availableMetricTargets,
        }),
      ),
      queryClient.ensureQueryData(getDeviceDetailsQuery(product.device.name)),
      isAdmin
        ? queryClient.ensureQueryData(
            listEventsQuery({
              resourceName: product.device.name,
            }),
          )
        : null,
    ]);

    return {
      metrics: metricsModel ?? undefined,
      deviceDetails: deviceDetails ?? undefined,
      events: events ?? undefined,
    };
  };
